// How a week of AxisCare visits becomes payroll lines — the one implementation.
//
// Moved out of pages/finance/PayrollRunDetail.js (2026-10-28) so the Monday
// payroll job (`open-payroll-runs`) builds a draft exactly the way the page's
// "Pull from AxisCare" does. No Supabase, no DOM: the function imports it
// through _shared/lib. What is written is lib/payroll/runs.js.
//
// The rules, each decided before this move:
//   • Pay the clocked window (visitHoursOn 'clocked'). No clock pair, no pay:
//     the visit is listed as an exception for a person to fix, not paid on its
//     scheduled hours.
//   • A clock span over a day is a wrong clock record, not a shift — held, not
//     paid (the Sep 6–12 run, 2026-10-01).
//   • Hourly pays hours × rate, per-visit pays visits × rate, and a salaried
//     person is paid their flat week once, on a client-less line, whether or
//     not they had visits. Their visit lines keep the hours at $0.
//   • Match a caregiver to staff by AxisCare id, then by a name only one staff
//     record carries. Two records with the name → left unmatched and reported.
//   • Each visit is paid at the rate in force on its date, from the person's
//     pay-rate history (staff_pay_rates); a salaried week at the rate in force
//     on its first day. Before the first rate on file, the earliest one.
//   • A visit on a service line that is not payable (a supervisory visit, a
//     nursing assessment on AxisCare's placeholder client) is not paid per
//     visit, and is listed on the run so nobody is left unpaid unseen.
//   • Overlapping clocked visits are flagged, except for a caregiver approved
//     to care for several clients at once (a parent of siblings).

import { visitHoursOn, clockWindow, hasClockPair } from '../visitHours.js';
import { visitCaregiverName, visitClientName } from '../axiscareVisits.js';

export const MAX_PAYABLE_VISIT_HOURS = 24;
export const SALARIED_PAY_TYPES = ['weekly', 'bi_weekly', 'monthly', 'annually'];

// Two clocked visits for one caregiver that overlap by more than this are the
// same hours paid twice, unless one of them is wrong. Small overlaps are a
// hand-over a few minutes early and are not worth anyone's attention.
export const OVERLAP_TOLERANCE_MINUTES = 15;

const round2 = (n) => Math.round(n * 100) / 100;
const nameKey = (s) => String(s || '').replace(/\s+/g, ' ').toLowerCase().trim();

/**
 * A salaried rate as one Sun–Sat week of pay. Hourly and per-visit are
 * computed per client line instead, so they return null.
 */
export const weeklyFlatPay = (rate, payType) => {
  if (rate == null) return null;
  switch (payType) {
    case 'weekly':    return rate;
    case 'bi_weekly': return rate / 2;
    case 'monthly':   return rate * 12 / 52;
    case 'annually':  return rate / 52;
    default:          return null;
  }
};

// A run is normally one week, but its dates can be edited to any range; a
// salaried person's week is scaled to the days the run spans, so a two-week
// catch-up run pays two weeks.
export const daysInRun = (run) => {
  if (!run?.week_start || !run?.week_end) return 7;
  const d = (new Date(`${run.week_end}T12:00:00Z`) - new Date(`${run.week_start}T12:00:00Z`)) / 86400000 + 1;
  return d > 0 ? d : 7;
};
export const flatPayForRun = (rate, payType, run) => {
  const weekly = weeklyFlatPay(rate, payType);
  return weekly == null ? null : weekly * (daysInRun(run) / 7);
};

/**
 * Was this person on staff at any point in the run's dates? Salaried pay is
 * paid for every week someone is employed — and only those weeks, which
 * matters the moment a past week is rebuilt: today's office staff were not
 * all on staff in March.
 */
export const employedDuring = (s, run) => {
  const start = s.start_date || null;
  const end = s.termination_date || s.end_date || null;
  if (start && start > run.week_end) return false;
  if (end && end < run.week_start) return false;
  if (!end && s.status && s.status !== 'active') return false;
  return true;
};

/**
 * The rate in force on a date, from rows of one person's pay-rate history.
 * Before the first row, the earliest — the best evidence of what came before.
 * No history at all: the profile's rate.
 */
export const rateOn = (history, date, s) => {
  if (history?.length) {
    const sorted = [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
    let row = sorted[0];
    for (const h of sorted) if (h.effective_from <= date) row = h;
    return { rate: Number(row.pay_rate), payType: row.pay_type || 'hourly' };
  }
  return { rate: s?.pay_rate != null ? parseFloat(s.pay_rate) : null, payType: s?.pay_type ?? 'hourly' };
};

/** staff_pay_rates rows grouped by staff_id. */
export const historyByStaff = (rows = []) => {
  const m = {};
  for (const r of rows) (m[r.staff_id] ||= []).push(r);
  return m;
};

/** The service codes AxisCare sends for lines that are never paid per visit. */
export const unpayableServiceCodes = (catalog = []) => new Set(
  catalog.filter(c => c.payable === false)
    .flatMap(c => [c.axiscare_service_code, ...(c.axiscare_service_aliases || [])]).filter(Boolean)
);

/**
 * Build a run's automatic lines from the week's visits.
 *
 * @param {object}   p
 * @param {object}   p.run          { id, week_start, week_end }
 * @param {object[]} p.visits       getVisitsByDateRange(..., { mode: 'payroll' }) results
 * @param {object[]} p.staffRows    every staff record, any status — a caregiver who
 *                                  has since left still worked the visit
 * @param {object[]} p.clientRows   { id, client_id, axiscare_client_id }
 * @param {object[]} p.manualLines  the run's is_manual lines, which a pull keeps
 * @param {object[]} [p.rateHistory] staff_pay_rates rows
 * @param {object[]} [p.catalog]     service_catalog rows, for which lines are payable
 * @returns {{ lines: object[], exceptions: object, autoSalaried: object[] }}
 */
export function buildPayrollLines({ run, visits = [], staffRows = [], clientRows = [], manualLines = [], rateHistory = [], catalog = [] }) {
  const history = historyByStaff(rateHistory);
  const notPaidCodes = unpayableServiceCodes(catalog);
  const clientByAxisId = {};
  for (const c of clientRows) if (c.axiscare_client_id != null) clientByAxisId[String(c.axiscare_client_id)] = c;
  const staffByAxisId = {}; const staffByName = {};
  for (const s of staffRows) {
    if (s.axiscare_id) {
      // An AxisCare id on two records (a rehire, a duplicate) prefers the active one.
      const prev = staffByAxisId[String(s.axiscare_id)];
      if (!prev || (prev.status !== 'active' && s.status === 'active')) staffByAxisId[String(s.axiscare_id)] = s;
    }
    if (s.full_name) (staffByName[nameKey(s.full_name)] ||= []).push(s);
  }
  const resolveStaff = (cgId, cgName) => {
    if (staffByAxisId[cgId]) return { s: staffByAxisId[cgId], ambiguous: false };
    const candidates = staffByName[nameKey(cgName)] || [];
    if (candidates.length === 1) return { s: candidates[0], ambiguous: false };
    // Several records share the name: one of them active and the rest gone is
    // still one person to pay.
    const active = candidates.filter(c => c.status === 'active');
    if (candidates.length > 1 && active.length === 1) return { s: active[0], ambiguous: false };
    return { s: null, ambiguous: candidates.length > 1 };
  };

  const exceptions = { unpaid: [], ambiguous: [], unmatched: [], noRate: [], overlaps: [], notPayable: [] };
  const grouped = {};
  const windowsByCaregiver = {};

  for (const v of visits) {
    if (v.removed) continue;
    if (!v.caregiver) continue;
    const cgName = visitCaregiverName(v);
    const clientName = visitClientName(v);
    const dateStr = (v.scheduledStartDate || v.startDate || '').slice(0, 10);
    // Listed, not just counted: a service code can be used for more than one
    // thing in AxisCare, and an unpaid visit nobody can see is a person unpaid.
    // A line that is never paid per visit is matched on its AxisCare code only
    // for visits booked against no CAERA client. AxisCare's "NA" is both the
    // nursing assessment (booked on a placeholder client, "Nursing Assessment")
    // and what a care visit for a private client with no service set carries
    // (Adekemi Oluphilips for Mabel Slay, G099, Oct 2026) — the second is
    // care, and is paid.
    const forRealClient = !!clientByAxisId[String(v.client?.id ?? '')];
    if (v.payable === false || (notPaidCodes.has(v.service?.code) && !forRealClient)) {
      exceptions.notPayable.push({ name: cgName, date: dateStr, client: clientName, service: v.service?.code || null, hours: Math.round(visitHoursOn(v, 'clocked') * 100) / 100 });
      continue;
    }
    const visitId = v.visitId ?? v.id ?? null;
    if (!hasClockPair(v)) {
      exceptions.unpaid.push({ name: cgName, date: dateStr, client: clientName, visitId, reason: 'no clock-in/out' });
      continue;
    }
    const hours = visitHoursOn(v, 'clocked');
    if (hours > MAX_PAYABLE_VISIT_HOURS) {
      exceptions.unpaid.push({ name: cgName, date: dateStr, client: clientName, visitId, reason: `clocked ${Math.round(hours)}h — clock record is wrong` });
      continue;
    }
    if (hours <= 0) {
      exceptions.unpaid.push({ name: cgName, date: dateStr, client: clientName, visitId, reason: 'clock-out is not after clock-in' });
      continue;
    }
    const cgId = String(v.caregiver.id || '');
    const clientId = String(v.client?.id || 'unknown');

    const w = clockWindow(v);

    if (!grouped[cgId]) {
      const { s, ambiguous } = resolveStaff(cgId, cgName);
      if (ambiguous) exceptions.ambiguous.push(cgName);
      const weekPay = s ? rateOn(history[s.id], run.week_start, s) : { rate: null, payType: 'hourly' };
      grouped[cgId] = {
        cgId, cgName, staff: s,
        // The week's footing — salaried or not — is the one in force on its first day.
        weekRate: weekPay.rate, weekPayType: weekPay.payType,
        role: s?.position_code || null,
        staffId: s?.id ?? null,
        employmentType: s?.employment_type ?? null,
        agencyId: s?.contracting_agency_id ?? null,
        agencyName: s?.contracting_agency?.name ?? null,
        clients: {},
      };
    }
    const cg = grouped[cgId];
    if (w && !cg.staff?.concurrent_care_approved) (windowsByCaregiver[cgId] ||= []).push({ ...w, name: cgName, client: clientName, date: dateStr });
    // Paid at the rate in force on the visit's date; a rate change mid-week
    // splits the client's line in two.
    const pay = cg.staff ? rateOn(history[cg.staff.id], dateStr || run.week_start, cg.staff) : { rate: null, payType: 'hourly' };
    const key = `${clientId}|${pay.rate}|${pay.payType}`;
    if (!cg.clients[key]) {
      const c = clientByAxisId[clientId] || null;
      cg.clients[key] = { clientId, clientName, ref: c?.client_id || null, uuid: c?.id || null, hours: 0, dates: new Set(), visits: 0, rate: pay.rate, payType: pay.payType };
    }
    const cl = cg.clients[key];
    cl.hours += hours; cl.visits += 1;
    if (dateStr) cl.dates.add(dateStr);
  }

  // The same hours clocked twice — two clients at once, or a visit entered twice.
  for (const wins of Object.values(windowsByCaregiver)) {
    wins.sort((a, b) => a.start - b.start);
    for (let i = 1; i < wins.length; i++) {
      const prev = wins[i - 1]; const cur = wins[i];
      const overlapMin = (Math.min(prev.end, cur.end) - cur.start) / 60000;
      if (overlapMin > OVERLAP_TOLERANCE_MINUTES) {
        exceptions.overlaps.push({ name: cur.name, date: cur.date, clients: [prev.client, cur.client], hours: round2(overlapMin / 60) });
      }
    }
  }

  const manualStaffIds = new Set(manualLines.map(l => l.staff_id).filter(Boolean));
  const lines = [];
  const salariedPaid = new Set();
  const base = { run_id: run.id, caregiver_profile_id: null, is_edited: false, is_manual: false };

  for (const cg of Object.values(grouped)) {
    if (!cg.staffId) exceptions.unmatched.push(cg.cgName);
    else if (cg.weekRate == null) exceptions.noRate.push(cg.cgName);
    const salaried = SALARIED_PAY_TYPES.includes(cg.weekPayType);
    const clientEntries = Object.values(cg.clients).sort((a, b) => a.clientName.localeCompare(b.clientName) || (a.dates.values().next().value || '').localeCompare(b.dates.values().next().value || ''));
    for (const cl of clientEntries) {
      const hrs = round2(cl.hours);
      // A salaried week pays its visits nothing; the salary is on its own line.
      const payType = salaried ? cg.weekPayType : cl.payType;
      const payRate = salaried ? cg.weekRate : cl.rate;
      let pay = null;
      if (salaried) pay = payRate != null ? 0 : null;
      else if (payType === 'hourly') pay = payRate != null ? round2(hrs * payRate) : null;
      else if (payType === 'per_visit') pay = payRate != null ? round2(cl.visits * payRate) : null;
      lines.push({
        ...base,
        staff_id: cg.staffId,
        caregiver_axiscare_id: cg.cgId,
        caregiver_name: cg.cgName,
        caregiver_role: cg.role,
        client_axiscare_id: cl.clientId,
        client_name: cl.clientName,
        client_ref: cl.ref,
        client_uuid: cl.uuid,
        visit_count: cl.visits,
        visit_dates: [...cl.dates].sort(),
        hours_raw: hrs,
        hours_adjusted: hrs,
        pay_rate: payRate,
        pay_type: payType,
        pay_total: pay,
        employment_type: cg.employmentType,
        contracting_agency_id: cg.agencyId,
        agency_name: cg.agencyName,
      });
    }
    if (salaried && cg.staff && !manualStaffIds.has(cg.staffId) && cg.weekRate != null) {
      lines.push(salaryLine(base, cg.staff, cg.weekRate, cg.weekPayType, run));
      salariedPaid.add(cg.staffId);
    }
  }

  // Salaried people with no visits this week — office staff, a nurse on admin.
  const autoSalaried = [];
  for (const s of staffRows) {
    if (salariedPaid.has(s.id) || manualStaffIds.has(s.id)) continue;
    if (!employedDuring(s, run)) continue;
    const pay = rateOn(history[s.id], run.week_start, s);
    if (!SALARIED_PAY_TYPES.includes(pay.payType) || pay.rate == null) continue;
    lines.push(salaryLine(base, s, pay.rate, pay.payType, run));
    autoSalaried.push(s);
  }

  return { lines, exceptions, autoSalaried };
}

const salaryLine = (base, s, rate, payType, run) => {
  const flat = flatPayForRun(rate, payType, run);
  return {
    ...base,
    staff_id: s.id,
    caregiver_axiscare_id: s.axiscare_id ? String(s.axiscare_id) : null,
    caregiver_name: s.full_name,
    caregiver_role: s.position_code ?? null,
    client_axiscare_id: null, client_name: null, client_ref: null, client_uuid: null,
    visit_count: 0, visit_dates: [],
    hours_raw: 0, hours_adjusted: 0,
    pay_rate: rate,
    pay_type: payType,
    pay_total: flat != null ? round2(flat) : null,
    employment_type: s.employment_type ?? null,
    contracting_agency_id: s.contracting_agency_id ?? null,
    agency_name: s.contracting_agency?.name ?? null,
  };
};

/** A run's totals, as payroll_runs stores them. */
export const payrollTotals = (lines = []) => ({
  total_hours: round2(lines.reduce((s, l) => s + Number(l.hours_adjusted || 0), 0)),
  total_pay: round2(lines.reduce((s, l) => s + Number(l.pay_total || 0), 0)),
  staff_count: new Set(lines.map(l => l.staff_id || l.caregiver_axiscare_id || l.caregiver_name)).size,
});

// ── Weeks ──────────────────────────────────────────────────────────────────
// Payroll weeks run Sunday to Saturday. Dates are plain YYYY-MM-DD strings,
// handled at noon UTC so no daylight-saving change moves a day.
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dow = (iso) => new Date(`${iso}T12:00:00Z`).getUTCDay();

/** The Sun–Sat week containing a date. */
export const payrollWeekOf = (iso) => {
  const start = addDays(iso, -dow(iso));
  return { start, end: addDays(start, 6) };
};

/** The last complete Sun–Sat week before `today` (on a Monday, the one that ended Saturday). */
export const lastCompletePayrollWeek = (today) => {
  let end = addDays(today, -1);
  while (dow(end) !== 6) end = addDays(end, -1);
  return { start: addDays(end, -6), end };
};

/** Every Sun–Sat week from the one containing `from` to the one ending on or before `to`, oldest first. */
export const payrollWeeksBetween = (from, to) => {
  const weeks = [];
  for (let w = payrollWeekOf(from); w.end <= to; w = { start: addDays(w.start, 7), end: addDays(w.end, 7) }) weeks.push(w);
  return weeks;
};
